import { useApiProblem, type AvailabilityConflict } from '@/shared/api';
import { Button } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { conflictLabels } from './helpers';

export type AvailabilityState = 'checking' | 'free' | 'hints' | 'blocked' | 'unchecked';

interface Props {
  state: AvailabilityState;
  nights: number;
  hardConflicts: AvailabilityConflict[];
  softConflicts: AvailabilityConflict[];
  override: boolean;
  onOverrideChange: (value: boolean) => void;
  onRetry: () => void;
  /** Warum die Prüfung nicht ankam – sonst probierte man blind gegen einen dauerhaften Fehler. */
  error?: unknown;
  offline?: boolean;
}

export function availabilityState(input: {
  loading: boolean;
  unavailable: boolean;
  hard: number;
  soft: number;
}): AvailabilityState {
  if (input.loading) return 'checking';
  // Ohne Antwort sind beide Konfliktlisten leer; „frei“ wäre eine nie geprüfte Behauptung.
  if (input.unavailable) return 'unchecked';
  if (input.hard > 0) return 'blocked';
  return input.soft > 0 ? 'hints' : 'free';
}

export const AVAILABILITY_SHORT: Record<AvailabilityState, string> = {
  checking: 'Wird geprüft',
  free: 'Frei',
  hints: 'Mit Hinweisen',
  blocked: 'Nicht buchbar',
  unchecked: 'Ungeprüft',
};

export const AVAILABILITY_TONE: Record<AvailabilityState, string> = {
  checking: 'bg-card-alt text-ink-soft border-line',
  free: 'bg-forest-soft text-forest-deep border-mint',
  hints: 'bg-amber-wash text-ink border-amber-tint',
  blocked: 'bg-sealwax-tint text-sealwax border-sealwax-tint',
  unchecked: 'bg-card-alt text-ink border-line',
};

const TEXT: Record<AvailabilityState, string> = {
  checking: 'Verfügbarkeit wird geprüft …',
  free: 'Geprüft gegen Buchungen, Sperren, Mindestnächte, An- und Abreisesperren, Verkaufsstopp und Wohnungsverbund.',
  hints: 'Die Prüfung hat weiche Regeln gefunden. Anlegen geht erst nach Bestätigung.',
  blocked: 'Bitte anderen Zeitraum oder anderes Apartment wählen.',
  unchecked:
    'Die Prüfung kam nicht an. Ohne Prüfung wird nicht angelegt – sonst gingen Hinweise wie Mindestnächte verloren.',
};

/** Ergebnis der Verfügbarkeitsprüfung in vier Zuständen (Board „Buchungen · Manuelle Buchung“). */
export function AvailabilityCard(props: Props) {
  const { state, nights, hardConflicts, softConflicts } = props;
  const problem = useApiProblem(props.error);
  const reason = props.offline
    ? 'Keine Verbindung.'
    : problem
      ? (problem.detail ?? `Code ${problem.title}`)
      : null;
  const title =
    state === 'free'
      ? `Frei · ${nights} ${nights === 1 ? 'Nacht' : 'Nächte'}`
      : state === 'hints'
        ? 'Buchbar mit Hinweisen'
        : state === 'unchecked'
          ? 'Verfügbarkeit ungeprüft'
          : AVAILABILITY_SHORT[state];
  const findings = state === 'blocked' ? hardConflicts : state === 'hints' ? softConflicts : [];
  return (
    <section
      aria-label="Verfügbarkeit"
      role={state === 'checking' ? 'status' : undefined}
      className={cn(
        'rounded-tile flex flex-col gap-1.5 border px-4 py-3.5',
        AVAILABILITY_TONE[state],
      )}
    >
      <p className="font-serif text-base font-bold">{title}</p>
      <p className="text-ink-soft text-[13px]">{TEXT[state]}</p>
      {findings.map((conflict, index) => (
        <p key={index} className="text-[13px]">
          · <b>{conflictLabels[conflict.kind]}</b>: {conflict.message}
        </p>
      ))}
      {state === 'hints' && (
        <label className="mt-1 flex min-h-11 cursor-pointer items-center gap-2 text-[13px] font-semibold">
          <input
            type="checkbox"
            className="accent-forest size-5 cursor-pointer"
            checked={props.override}
            onChange={(event) => props.onOverrideChange(event.target.checked)}
          />
          Hinweise gelesen – trotzdem anlegen
        </label>
      )}
      {state === 'unchecked' && reason && <p className="text-[13px] font-semibold">{reason}</p>}
      {state === 'unchecked' && (
        <Button variant="secondary" className="self-start" onClick={props.onRetry}>
          Erneut prüfen
        </Button>
      )}
    </section>
  );
}
